import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { createTechPrepUser, db } from "../../config/firebase";
import {
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const TpAddUser = ({ onSuccess }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [userTypes, setUserTypes] = useState({
    staff: false,
    coordinator: false,
    instructor: false,
    admin: false,
    pathwayManager: false,
  });
  const [partner, setPartner] = useState("");
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");

  // Coordinators are tied to a Tech Prep Partner org, so the dropdown only
  // offers partners currently marked active in the Partners collection.
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const snap = await getDocs(collection(db, "TpPartners"));
        const names = snap.docs
          .map((d) => d.data())
          .filter((p) => p.Active_yn === "y" && p.Tech_Prep_Partner)
          .map((p) => p.Tech_Prep_Partner)
          .sort((a, b) => a.localeCompare(b));
        setPartnerOptions(names);
      } catch (e) {
        console.error("Error loading partners:", e);
        setPartnerOptions([]);
      }
    };
    loadPartners();
  }, []);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, "");

    // Format as (xxx) xxx-xxxx
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6,
      )}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const handleUserTypeChange = (type) => (e) => {
    setUserTypes((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    console.log("Attempting to create user with data:", {
      email,
      firstName,
      lastName,
      phone,
      userTypes,
      passwordLength: password.length,
    });

    try {
      const result = await createTechPrepUser(
        email,
        password,
        firstName,
        lastName,
        phone,
        userTypes,
        userTypes.coordinator ? partner : "",
      );

      console.log("User creation result:", result);

      if (result.success) {
        setSnackbarMessage("User added successfully");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setPassword("");
        setUserTypes({
          staff: false,
          coordinator: false,
          instructor: false,
          admin: false,
          pathwayManager: false,
        });
        setPartner("");
        if (onSuccess) onSuccess("User added successfully");
      } else {
        console.error("User creation failed:", result.message);
        setError(result.message);
        setSnackbarMessage(result.message);
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error during user creation:", err);
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
      const errorMessage = err.message || "Error adding user";
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Box component="form" onSubmit={handleSubmit} mb={2}>
      <TextField
        label="First Name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        fullWidth
        margin="dense"
      />
      <TextField
        label="Last Name"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
        fullWidth
        margin="dense"
      />
      <TextField
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        margin="dense"
      />
      <TextField
        label="Phone"
        value={phone}
        onChange={handlePhoneChange}
        fullWidth
        margin="dense"
        placeholder="(xxx) xxx-xxxx"
        inputProps={{ maxLength: 14 }}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        margin="dense"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={userTypes.staff}
            onChange={handleUserTypeChange("staff")}
          />
        }
        label="Staff"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={userTypes.coordinator}
            onChange={handleUserTypeChange("coordinator")}
          />
        }
        label="Coordinator"
      />
      {userTypes.coordinator && (
        <FormControl fullWidth margin="dense">
          <InputLabel>Partner</InputLabel>
          <Select
            value={partner}
            onChange={(e) => setPartner(e.target.value)}
            label="Partner"
          >
            {partnerOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={userTypes.instructor}
            onChange={handleUserTypeChange("instructor")}
          />
        }
        label="Instructor"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={userTypes.admin}
            onChange={handleUserTypeChange("admin")}
          />
        }
        label="Admin"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={userTypes.pathwayManager}
            onChange={handleUserTypeChange("pathwayManager")}
          />
        }
        label="Pathway Manager"
      />
      {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        disabled={loading}
      >
        Add User
      </Button>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TpAddUser;
